import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useNavigate } from "react-router-dom";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import magazineStyles from "../../styles/magazine.css?raw";


pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

const INITIAL_MOBILE_SCALE = 1;

function formatIssueMonth(issue) {
  if (!issue?.month || !issue?.year) return "Magazine Issue";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric"
  }).format(new Date(issue.year, issue.month - 1, 1));
}

export default function MagazineReader({ issue }) {
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(INITIAL_MOBILE_SCALE);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(960);
  const [isLoading, setIsLoading] = useState(true);
  const [readyPages, setReadyPages] = useState({});
  const [error, setError] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const readerRef = useRef(null);
  const viewportRef = useRef(null);
  const pageRefs = useRef({});
  const pdfUrl = issue?.pdf_url || "/assets/pdf/sample.pdf";
  const issueTitle = issue?.title || `CGES.CLUB Magazine - ${formatIssueMonth(issue)}`;

  function onLoadSuccess({ numPages: loadedPages }) {
    setNumPages(loadedPages);
    setIsLoading(false);
    setReadyPages({});
    setPageNumber((current) => Math.min(Math.max(current, 1), loadedPages));
    setError("");
  }

  function onLoadError() {
    setIsLoading(false);
    setError("Unable to load the magazine PDF.");
  }

  useEffect(() => {
    const updateWidth = () => {
      if (!viewportRef.current) return;

      const nextWidth = Math.floor(viewportRef.current.clientWidth);
      if (nextWidth > 0) {
        setContainerWidth((current) =>
          Math.abs(current - nextWidth) > 6 ? nextWidth : current
        );
      }
      setIsMobileView(window.innerWidth <= 767);
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;

    if (resizeObserver && viewportRef.current) {
      resizeObserver.observe(viewportRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateWidth);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setScale((current) => (current === INITIAL_MOBILE_SCALE ? current : INITIAL_MOBILE_SCALE));
    }
  }, [isMobileView]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === readerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (
        event.target instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName)
      ) {
        return;
      }

      if (event.key === "ArrowRight") {
        goToNextPage();
      }

      if (event.key === "ArrowLeft") {
        goToPreviousPage();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [numPages, pageNumber]);

  const changePage = (nextPageNumber) => {
    if (!numPages) return;

    const clampedPage = Math.min(Math.max(nextPageNumber, 1), numPages);
    const targetPage = pageRefs.current[clampedPage];
    if (targetPage) {
      targetPage.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
    setPageNumber(clampedPage);
  };

  const goToPreviousPage = () => changePage(pageNumber - 1);
  const goToNextPage = () => changePage(pageNumber + 1);
  const zoomOut = () => setScale((current) => Math.max(0.75, +(current - 0.1).toFixed(2)));
  const zoomIn = () => setScale((current) => Math.min(2, +(current + 0.1).toFixed(2)));
  const fitToWidth = () => setScale(isMobileView ? INITIAL_MOBILE_SCALE : 1);

  const toggleFullscreen = async () => {
    if (!readerRef.current) return;

    if (document.fullscreenElement === readerRef.current) {
      await document.exitFullscreen?.();
      return;
    }

    await readerRef.current.requestFullscreen?.();
  };

  const progress = numPages ? Math.round((pageNumber / numPages) * 100) : 0;
  const windowHeight = typeof window !== "undefined" ? window.innerHeight : 1080;
  const basePageWidth = isMobileView
    ? Math.max(260, Math.min(containerWidth - 16, 980))
    : Math.max(320, Math.min(containerWidth - 32, 980));
  const pageShellWidth = Math.round(basePageWidth * scale);
  const pageShellMinHeight = Math.round(pageShellWidth * 1.414);
  const viewportHeight = Math.round(
    Math.min(
      isMobileView
        ? windowHeight * (isFullscreen ? 0.9 : 0.78)
        : windowHeight * 0.78,
      pageShellMinHeight + (isMobileView ? (isFullscreen ? 20 : 36) : 64)
    )
  );

  useEffect(() => {
    if (!numPages || !viewportRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visibleEntry) return;

        const nextPageNumber = Number(visibleEntry.target.dataset.pageNumber);
        if (nextPageNumber) {
          setPageNumber((current) => (current === nextPageNumber ? current : nextPageNumber));
        }
      },
      {
        root: viewportRef.current,
        threshold: [0.25, 0.5, 0.75]
      }
    );

    Object.values(pageRefs.current).forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [numPages, scale]);

  const markPageReady = (page) => {
    setReadyPages((current) => {
      if (current[page]) return current;
      return { ...current, [page]: true };
    });
  };

  return (
    <>
    <style>{magazineStyles}</style>
    <section
      ref={readerRef}
      className={`magazine-panel magazine-pdf-reader${isFullscreen ? " is-fullscreen" : ""}`}
    >
      <div className="magazine-pdf-reader__topbar">
        <button
          type="button"
          className="btn magazine-btn magazine-btn--ghost magazine-pdf-reader__back-btn"
          onClick={() => navigate("/magazine")}
        >
          <span aria-hidden="true">&lt;</span>
          <span>Back to Magazine</span>
        </button>
        <h2 className="magazine-pdf-reader__title">{issueTitle}</h2>
        <div className="magazine-pdf-reader__meta">
          <span className="magazine-chip">{numPages ? `${numPages} Pages` : "Loading..."}</span>
          <span className="magazine-chip magazine-chip--accent">{progress}% Read</span>
        </div>
      </div>

      <div className="magazine-pdf-reader__toolbar">
        <div className="magazine-pdf-reader__group">
          <button
            type="button"
            className="btn magazine-btn magazine-btn--secondary magazine-pdf-reader__nav-btn"
            onClick={goToPreviousPage}
            disabled={pageNumber <= 1}
            aria-label="Previous page"
          >
            <span aria-hidden="true">&lt;</span>
          </button>
          <div className="magazine-pdf-reader__page-indicator">
            Page {pageNumber}{numPages ? ` / ${numPages}` : ""}
          </div>
          <button
            type="button"
            className="btn magazine-btn magazine-btn--secondary magazine-pdf-reader__nav-btn"
            onClick={goToNextPage}
            disabled={!numPages || pageNumber >= numPages}
            aria-label="Next page"
          >
            <span aria-hidden="true">&gt;</span>
          </button>
        </div>

        <div className="magazine-pdf-reader__group magazine-pdf-reader__group--desktop">
          {!isMobileView ? (
            <>
              <button type="button" className="btn magazine-btn magazine-btn--icon" onClick={zoomOut}>
                -
              </button>
              <button type="button" className="btn magazine-btn magazine-btn--ghost" onClick={fitToWidth}>
                Fit
              </button>
              <button type="button" className="btn magazine-btn magazine-btn--icon" onClick={zoomIn}>
                +
              </button>
              <div className="magazine-pdf-reader__zoom">{Math.round(scale * 100)}%</div>
            </>
          ) : null}
          <button
            type="button"
            className="btn magazine-btn magazine-btn--primary"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
        </div>
      </div>

      <div className="magazine-pdf-reader__progress">
        <div className="progress">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div
        ref={viewportRef}
        className="magazine-pdf-reader__viewport"
        style={{
          height: `${viewportHeight}px`,
          touchAction: isMobileView ? "pinch-zoom pan-x pan-y" : "pan-y"
        }}
      >
        {isLoading ? <div className="magazine-pdf-reader__state">Loading issue...</div> : null}
        {error ? (
          <div className="magazine-pdf-reader__state magazine-pdf-reader__state--error">{error}</div>
        ) : null}

        <Document file={pdfUrl} loading="" noData="" onLoadSuccess={onLoadSuccess} onLoadError={onLoadError}>
          {!error ? (
            <div className="magazine-pdf-reader__book">
              {Array.from({ length: numPages || 0 }, (_, index) => {
                const currentPage = index + 1;

                return (
                  <div
                    key={`page-${currentPage}`}
                    ref={(node) => {
                      if (node) {
                        pageRefs.current[currentPage] = node;
                      } else {
                        delete pageRefs.current[currentPage];
                      }
                    }}
                    data-page-number={currentPage}
                    className={`magazine-pdf-reader__page-shell${
                      currentPage === pageNumber ? " is-active" : ""
                    }`}
                    style={{
                      width: `${pageShellWidth}px`,
                      minHeight: readyPages[currentPage] ? undefined : `${pageShellMinHeight}px`
                    }}
                  >
                    {!readyPages[currentPage] ? (
                      <div className="magazine-pdf-reader__page-placeholder" />
                    ) : null}
                    <Page
                      className="magazine-pdf-reader__page"
                      pageNumber={currentPage}
                      width={basePageWidth}
                      scale={scale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      loading=""
                      onRenderSuccess={() => markPageReady(currentPage)}
                      onRenderError={() => markPageReady(currentPage)}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </Document>
      </div>

    </section>
    </>
  );
}
